import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import {
  Grid,
  Paper,
  Select,
  MenuItem,
  Typography,
  TextField,
  Box,
  Divider,
  Button,
  Modal,
  CircularProgress,
  Link as Anchor,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "./style.css";
import params from "../../../setup/config/params";
import { nullToEmptyString } from "../../../setup/helpers";
import { messageShow } from "../../common/api/actions";
import {
  signup,
  signupFinish,
  googleSignup,
  googleSignupFinish,
} from "../api/actions/mutation";
import AuthCheck from "../../auth/AuthCheck";
import routes from "../api/routes";
import { fetchRoles } from "../../roles/api/query";
import { useGoogleClientId } from "../../../setup/config/GoogleProviderWrapper";
import GoogleSignupButton from "./GoogleSignupButton";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "white",
  boxShadow: 24,
  borderRadius: "8px",
  p: 4,
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: {
    xs: "90%",
    sm: "470px",
  },
};

function Signup({ signup, signupFinish, messageShow }) {
  const history = useHistory();
  const googleClientId = useGoogleClientId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    role: "",
    otp: "",
  });
  const [roles, setRoles] = useState([]);
  const [mobile, setMobile] = useState("");
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [googleData, setGoogleData] = useState(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [phoneModalError, setPhoneModalError] = useState("");
  const [isPhoneModalLoading, setIsPhoneModalLoading] = useState(false);
  const [isMobileExist, setIsMobileExist] = useState(false);

  useEffect(() => {
    const getRoles = async () => {
      const fetchedRoles = await fetchRoles();
      const filtered = fetchedRoles.filter((r) => r.name !== "Super Admin");
      setRoles(filtered);
    };
    getRoles();
  }, []);

  const onType = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const { name, email, mobile, password, role } = form;
      const { data } = await signup({ name, email, mobile, password, role });

      if (data.success) {
        setIsSubmitted(true);
      } else {
        messageShow(data.message);
      }
    } catch(err) {
      console.error("Error in google signup: ", err)
      messageShow("Some error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const finishSignup = async () => {
    try {
      const { email, mobile, otp } = form;
      const { data } = await signupFinish({ email, mobile, otp });

      if (data.success) {
        setIsVerified(true);
        setIsSubmitted(false);
        setForm((prev) => ({ ...prev, role: "" }));
      } else {
        messageShow(data.message);
      }
    } catch(err) {
      console.error("Error in google signup finish: ", err)
      messageShow("Some error occurred. Please try again.");
    }
  };

  const verifyOTP = async (event) => {
    event.preventDefault();
    if (form.otp.length === 4) {
      finishSignup();
    } else {
      alert("Invalid OTP. Please try again.");
    }
  };

  const handleGoogleStart = () => {
    setIsGoogleLoading(true);
    setPhoneModalError("");
  }

  const handleGoogleSuccess = (data) => {
    setGoogleData(data);
    setMobile(data.phoneNumber || "");
    setIsGoogleLoading(false);
    setIsPhoneModalOpen(true);   
  };

  const handleGoogleError = (message) => {
    messageShow(message);
    setIsGoogleLoading(false);
  }

  const handleConfirmSignup = async () => {
    if (isPhoneModalLoading) return;
    setPhoneModalError("");
    setIsMobileExist(false);

    try {
      setIsPhoneModalLoading(true);
      const payload = { ...googleData, mobile, role: form.role };
      const response = await googleSignupFinish(payload);
      const resData = response.resData;

      if (response.success) {
        setIsPhoneModalOpen(false);
        setIsVerified(true);
      } else if (resData?.type === "MOBILE_EXIST") {
        setPhoneModalError(response.message);
        setIsMobileExist(true);
      } else {
        setPhoneModalError(
          response.message || "Something went wrong. Please try again."
        );
      }
    } catch (err) {
      console.error("Signup Finish Error: ", err);
      setPhoneModalError("Signup failed. Please try again.");
    } finally {
      setIsPhoneModalLoading(false);
    }
  };

  return (
    <div className="signup-root">
      <Paper className="signup-container">
        {!isSubmitted && !isVerified && (
          <Typography
            variant="subtitle1"
            color="inherit"
            className="signup-heading"
          >
            {`Signup on ${params.site.name}`}
          </Typography>
        )}

        {isVerified ? (
          <div className="signup-otp-container">
            <CheckCircleIcon className="signup-icon-success" />
            <Typography
              variant="subtitle1"
              color="inherit"
              className="signup-heading"
            >
              You are signed up! Please contact your Admin team to activate your user.
            </Typography>
          </div>
        ) : isSubmitted ? (
          <div className="signup-otp-container">
            <form onSubmit={verifyOTP}>
              <Typography variant="h6" className="signup-otp-heading">
                Enter the verification code
              </Typography>
              <Typography variant="body1" className="signup-otp-text">
                We have sent a verification code to your {form.email}.<br />
                If you didn’t receive a code, please try again.
              </Typography>
              <TextField
                name="otp"
                value={form.otp}
                onChange={onType}
                label="Enter OTP"
                placeholder="Enter 4-digit code"
                required
                margin="dense"
                autoComplete="off"
                fullWidth
                variant="standard"
              />

              <Grid
                container
                spacing={2}
                justifyContent="flex-end"
                className="signup-buttons-container"
              >
                <Grid item>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setIsSubmitted(false)}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    Verify
                  </Button>
                </Grid>
              </Grid>
            </form>
          </div>
        ) : (
          <>
          
            {googleClientId && 
              <>
                <GoogleSignupButton
                  onGoogleStart={handleGoogleStart}
                  onGoogleSuccess={handleGoogleSuccess}
                  onGoogleError={handleGoogleError}
                />
                <Divider sx={{ my: 2, fontSize: "14px", color: "#9b9a9aff" }}> Or with email and password </Divider> 
            </>}

            <form onSubmit={handleSignup}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  value={nullToEmptyString(form.name)}
                  onChange={onType}
                  label="Name"
                  placeholder="Enter name"
                  required
                  margin="dense"
                  autoComplete="off"
                  fullWidth
                  autoFocus
                  variant="standard"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="mobile"
                  value={nullToEmptyString(form.mobile)}
                  onChange={onType}
                  label="Mobile"
                  placeholder="Enter 10 digit mobile number"
                  required
                  margin="dense"
                  autoComplete="off"
                  fullWidth
                  inputProps={{ maxLength: 10 }}
                  variant="standard"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="email"
                  type="email"
                  value={nullToEmptyString(form.email)}
                  onChange={onType}
                  label="Email"
                  placeholder="Enter email"
                  required
                  margin="dense"
                  autoComplete="off"
                  fullWidth
                  variant="standard"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="password"
                  type="password"
                  value={nullToEmptyString(form.password)}
                  onChange={onType}
                  label="Password"
                  placeholder="Enter password"
                  required
                  margin="dense"
                  autoComplete="off"
                  fullWidth
                  variant="standard"
                />
              </Grid>

              <Grid item xs={12} sx={{ textAlign: "left", mt: 2 }}>
                <Select
                  name="role"
                  value={form.role}
                  onChange={onType}
                  displayEmpty
                  fullWidth
                  required
                  variant="standard"
                >
                  <MenuItem value="" disabled>
                    Select role*
                  </MenuItem>
                  {roles.map((r) => (
                    <MenuItem key={r._id} value={r._id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} className="signup-buttons-container">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Signing up..." : "Sign Up"}
                </Button>
              </Grid>
            </form>
          </>
        )}
      </Paper>

      <Link to={routes.userLogin.path}>
        <Anchor component="span">Already have an account - Login</Anchor>
      </Link>

      <AuthCheck />

      <Modal open={isGoogleLoading}>
        <Box sx={style}>
          <CircularProgress size={30} color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, mt: 3 }}>
            Fetching your details from Google...
          </Typography>
          <Typography variant="body2" sx={{ color: "gray" }}>
            Please wait a moment.
          </Typography>
        </Box>
      </Modal>

      <Modal open={isPhoneModalOpen} onClose={() => {}} disableEscapeKeyDown>
        <Box sx={style}>
          <Typography
            variant="h6"
            sx={{ mb: 5, fontWeight: 500, textAlign: "center" }}
          >
            Confirm your mobile number & Select role
          </Typography>
          <TextField
            label="Mobile"
            type="tel"
            required
            value={nullToEmptyString(mobile)}
            inputProps={{ maxLength: 10 }}
            placeholder="Enter 10 digit mobile number"
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              setMobile(value);
              setPhoneModalError(""); 
              setIsMobileExist(false);
            }}
            fullWidth
            variant="standard"
            sx={{ mb: 3 }}
          />
          <Select
            name="role"
            value={form.role}
            onChange={onType}
            displayEmpty
            fullWidth
            required
            variant="standard"
            sx={{ mb: 3, textAlign: "left" }}
          >
            <MenuItem value="" disabled>
              Select role*
            </MenuItem>
            {roles.map((r) => (
              <MenuItem key={r._id} value={r._id}>
                {r.name}
              </MenuItem>
            ))}
          </Select>

          {phoneModalError && (
            <Typography
              sx={{
                color: "red",
                fontSize: "15px",
                textAlign: "center",
                mb: 3,
                maxWidth: "90%",
              }}
            >
              {phoneModalError}
            </Typography>
          )}
          {isMobileExist ? (
            <Button
              variant="contained"
              fullWidth
              color="primary"
              onClick={() => history.push("/login")}
              sx={{ mt: 2 }}
            >
              Login
            </Button>
          ) : (
            <Button
              variant="contained"
              fullWidth
              onClick={handleConfirmSignup}
              disabled={!mobile.trim() || !form.role.trim() || isPhoneModalLoading}
              sx={{ mt: 2 }}
            >
              {isPhoneModalLoading ? (
                <>
                  <CircularProgress
                    size={22}
                    sx={{ position: "absolute", left: "15%" }}
                  />
                  Signing up...
                </>
              ) : (
                "Confirm & Sign Up"
              )}
            </Button>
          )}
        </Box>
      </Modal>
    </div>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {
  signup,
  signupFinish,
  messageShow,
  googleSignup,
  googleSignupFinish,
})(Signup);
